import { useCallback, useEffect, useMemo, useState } from 'react';
import { customEvent, EventType } from '@core/customEvent';
import { isFalsy } from '@core/helpers/boolean';
import { isIframe } from '@core/window';
import { PIP_IFRAME_NAME } from './constants';
import { hasPIPIframe } from './hasPIPIframe';
import { usePIPControls } from './usePIPControls';
export var TargetEnum;
(function (TargetEnum) {
    TargetEnum["TOP"] = "_top";
    TargetEnum["PIP_IFRAME"] = "fw-iframe";
})(TargetEnum || (TargetEnum = {}));
/**
 * Hook providing the url, target and onClick handler for any links that
 * can potentially trigger Picture-in-Picture (PIP) mode. E.g. CTA, links
 * in chat messages...
 * @param IProps
 */
export function usePIPTarget(props) {
    const { params } = props;
    const { enter: enterPIP } = usePIPControls({
        enabled: params?.pip && params.pip_navigate,
        params: params || {},
    });
    const [url, origin, isOriginMatch] = useMemo(() => {
        const url = props.url && props.url.startsWith('/') && !props.url.startsWith('//')
            ? window.location.origin + props.url
            : props.url;
        let origin = 'unknown';
        try {
            origin = url ? new URL(url).origin : '';
        }
        catch (err) { }
        const isOriginMatch = typeof window !== 'undefined' &&
            (origin === window.location.origin || origin.endsWith('.myshopify.com'));
        return [url, origin, isOriginMatch];
    }, [props.url]);
    const [target, setTarget] = useState(isOriginMatch ? '_self' : '_blank');
    useEffect(() => {
        if (params?.pip_target &&
            ['_self', '_blank'].includes(params?.pip_target)) {
            setTarget(params.pip_target);
            return;
        }
        if (isOriginMatch) {
            if (hasPIPIframe()) {
                // In case PIP is active, lets target all links
                // to correct iframe using its name.
                setTarget(TargetEnum.PIP_IFRAME);
            }
            else if (isIframe()) {
                setTarget(TargetEnum.TOP);
            }
            else {
                // Otherwise load the page in current
                setTarget('_self');
            }
        }
        else {
            setTarget('_blank');
        }
    }, [isOriginMatch, params?.pip_target]);
    const onClick = useCallback((event) => {
        if (isOriginMatch) {
            customEvent({
                type: EventType.FW_NAVIGATE,
                data: {
                    url,
                },
            });
            if (isFalsy(params?.pip) || params?.pip_navigate === false) {
                if (params?.pip_navigate === false) {
                    event?.preventDefault();
                    event?.stopPropagation();
                }
                return;
            }
            if (isFalsy(params?.pip_target)) {
                event?.preventDefault();
                event?.stopPropagation();
                enterPIP(url);
            }
        }
    }, [isOriginMatch, params, url, enterPIP]);
    return { url, origin, isOriginMatch, target, onClick };
}
